import { type FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useBuyExtendTimeMutation } from '@/api/queries/contract';
import { tgemImages } from '@/assets/img/wallet';
import {
  type PaidSpecContractType,
  type SpecContractType,
} from '@/common/types/contract';
import { ModalContext } from '@/components/Modals/Modal/config';
import { H3 } from '@/components/Typography/Typography';
import { PurpleButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import { addToBalance } from '@/redux/reducers/userReducer';
import {
  selectSpecContractDataByKind,
  selectSpecContractPriceForExtentionTime,
} from '@/redux/selectors/contractSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/store';

interface BuyExtendTimeForContractProps {
  kind: SpecContractType | PaidSpecContractType;
}

const BuyExtendTimeForContract: FC<BuyExtendTimeForContractProps> = ({
  kind,
}) => {
  const { closeModal } = useContext(ModalContext);
  const tgemBalance = useAppSelector((state) => state.user.user?.balance.tgem);
  const { t } = useTranslation();
  const price = useAppSelector((state) =>
    selectSpecContractPriceForExtentionTime(state, kind),
  );
  const dispatch = useAppDispatch();
  const [buyExtraTime, { isLoading }] = useBuyExtendTimeMutation();
  const contract = useAppSelector((state) =>
    selectSpecContractDataByKind(state, kind),
  );
  const notEnoughtFounds = (tgemBalance || 0) < (price || 0);

  const handleClick = (): void => {
    if (!contract || notEnoughtFounds) {
      return;
    }

    buyExtraTime({ id: contract.id })
      .unwrap()
      .then(() => {
        dispatch(addToBalance({ tgem: (price || 0) * -1 }));
        closeModal();
      })
      .catch(() =>
        showToast('Something went wrong, please try again', {
          img: tgemImages.icon.png,
          webp: tgemImages.icon.webp,
        }),
      );
  };

  return (
    price && (
      <PurpleButton
        inactive={notEnoughtFounds}
        disabled={notEnoughtFounds || isLoading}
        isLoading={isLoading}
        onClick={handleClick}
        className="flex items-end gap-1 justify-center !w-[calc(100%_-_2rem)]"
      >
        <H3 weight="extrabold">{t('Extend for')}</H3>
        <Picture
          className="w-6 h-6"
          img={tgemImages.icon.png}
          webp={tgemImages.icon.webp}
          alt="tgem"
        />
        <H3 weight="extrabold">{price}</H3>
      </PurpleButton>
    )
  );
};

export { BuyExtendTimeForContract };
